'use client';

import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { useGetListsQuery } from 'src/api/redux/lists/listsApi';
import { Input } from 'src/components';
import {
  faListUl,
  faCaretDown,
  faChevronRight,
  faUserGroup,
  faCheck,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { useAuth } from 'src/context/auth';
import { useRouter, usePathname } from 'next/navigation';
import useLists from 'src/hooks/lists/useLists';
import { useDebounce } from 'use-debounce';
import { ListItem } from './components';

interface ListOption {
  id: string;
  name: string;
  path: string;
  type?: string;
  num_items: number;
  owner?: {
    id: number;
    first_name: string;
    last_name: string;
  };
}

interface GroupedLists {
  myLists: ListOption[];
  sharedLists: ListOption[];
}

const ListsAutocomplete = () => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { user } = useAuth();
  const router = useRouter();
  const pathname = usePathname();
  const { selectedList, storedList, setSelectedList } = useLists();
  const isMedScoutUser =
    user?.is_superuser || user?.permissions?.is_support_user;
  const isManagerUser = user?.permissions?.is_manager;

  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm] = useDebounce(searchTerm, 300);
  const [isTeamListsSelected, setIsTeamListsSelected] = useState(false);

  // Build query params for API - only include 'q' if search term has min length
  const queryParams = useMemo(() => {
    const trimmed = debouncedSearchTerm.trim();
    return trimmed.length >= 2 ? { q: trimmed } : {};
  }, [debouncedSearchTerm]);

  // Fetch lists with search params
  const { data: lists } = useGetListsQuery(queryParams);

  const searchState = useMemo(
    () => ({
      isSearching: searchTerm.length >= 2,
      hasMinLength: searchTerm.length >= 2,
      isEmpty: searchTerm.length === 0,
    }),
    [searchTerm]
  );

  // Handler for grouping and sorting lists with search filtering
  const getGroupedLists = useCallback((): GroupedLists => {
    if (!lists?.results) return { myLists: [], sharedLists: [] };

    // My lists
    const myLists = lists.results
      ?.filter(
        (list) =>
          list.owner.id === user?.id &&
          list.type !== 'smart'
      )
      .map((list) => ({
        id: list.id.toString(),
        name: list.name,
        num_items: list.num_items,
        path: `/lists/my/${list.id}`,
        type: list.type,
        owner: {
          id: list.owner.id,
          first_name: list.owner.first_name,
          last_name: list.owner.last_name,
        },
      }))
      .sort((a, b) => a.name.localeCompare(b.name));

    // Shared lists
    const sharedLists = lists.results
      ?.filter((list) => {
        const sharedWith = list.shared_users?.map((u) => u.id) || [];
        return (
          list.owner.id !== user?.id &&
          sharedWith.includes(user?.id)
        );
      })
      .map((list) => ({
        id: list.id.toString(),
        name: list.name,
        num_items: list.num_items,
        path: `/lists/shared/${list.id}`,
        type: list.type,
        owner: {
          id: list.owner.id,
          first_name: list.owner.first_name,
          last_name: list.owner.last_name,
        },
      }))
      .sort((a, b) => a.name.localeCompare(b.name));

    return { myLists, sharedLists };
  }, [lists, user]);

  // Memoized grouped lists with search filtering
  const groupedLists = useMemo(
    () => getGroupedLists(),
    [getGroupedLists, lists]
  );

  // Handler for checking if we're on team lists page
  const checkTeamListsPage = useCallback(() => {
    const isTeam =
      pathname === '/lists/team' ||
      (pathname?.startsWith('/lists/team') && !pathname?.match(/\/lists\/team\/.+/));

    setIsTeamListsSelected(isTeam);

    if (isTeam && selectedList) {
      setSelectedList(null);
    }
  }, [pathname, selectedList, setSelectedList]);

  // Effect for team lists page check
  useEffect(() => {
    checkTeamListsPage();
  }, [checkTeamListsPage]);

  // Handler for clicking outside
  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        // Only clear search if it was actual searching, not selection
        if (searchTerm && !selectedList?.name?.includes(searchTerm)) {
          setSearchTerm('');
        }
      }
    },
    [searchTerm, selectedList]
  );

  // Effect for click outside
  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [handleClickOutside]);

  // Handler for list selection
  const handleListSelect = useCallback(
    (list: ListOption) => {
      setSelectedList(list);
      setIsTeamListsSelected(false);
      setIsOpen(false);
      setSearchTerm('');
      router.push(list.path);
    },
    [router, setSelectedList]
  );

  // Handler for input change
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchTerm(value);
      setIsOpen(true);
    },
    []
  );

  // Handler for input focus
  const handleInputFocus = useCallback(() => {
    setIsOpen(true);
  }, []);

  // Handler for toggle open
  const handleToggleOpen = useCallback(() => {
    setIsOpen((prev) => {
      if (prev) {
        setSearchTerm('');
      }
      return !prev;
    });
    inputRef.current?.focus();
  }, []);

  // Handler for team lists selection
  const handleTeamListsSelect = useCallback(() => {
    router.push('/lists/team');
    setIsTeamListsSelected(true);
    setSelectedList(null);
    setIsOpen(false);
  }, [router, setSelectedList]);

  // Handler for display value
  const getDisplayValue = useCallback(() => {
    if (isOpen) return searchTerm; // Always show search term when open
    if (isTeamListsSelected) return 'Team Lists';
    return selectedList?.name || storedList?.name || '';
  }, [isOpen, searchTerm, isTeamListsSelected, selectedList?.name, storedList?.name]);

  return (
    <div
      ref={dropdownRef}
      className="relative"
      data-testid="lists-autocomplete"
    >
      <div className="w-72 h-12">
        <Input
          ref={inputRef}
          placeholder={
            searchState.isSearching
              ? 'Searching...'
              : 'Search lists... (min 2 chars)'
          }
          className="h-full rounded-full text-primary font-medium shadow-sm hover:shadow-md transition-shadow duration-200"
          slotProps={{
            input: { className: 'truncate' },
          }}
          value={getDisplayValue()}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          aria-label="Search and select lists"
          aria-expanded={isOpen}
          aria-controls="lists-autocomplete-dropdown"
          aria-autocomplete="list"
          aria-haspopup="listbox"
          data-testid="lists-autocomplete-input"
          startDecorator={
            <Icon
              icon={faListUl}
              className="text-neutral-500 flex-shrink-0"
              aria-hidden="true"
            />
          }
          endDecorator={
            <Icon
              size="sm"
              icon={faCaretDown}
              className={`text-neutral-500 transition-transform duration-200 flex-shrink-0 cursor-pointer ${
                isOpen ? '-rotate-90' : 'rotate-0'
              }`}
              onClick={handleToggleOpen}
              aria-hidden="true"
            />
          }
        />
      </div>

      {isOpen && (
        <div
          id="lists-autocomplete-dropdown"
          role="listbox"
          aria-label="Available lists"
          className="absolute z-[100] w-96 min-w-full max-w-xl mt-2 bg-white rounded-lg shadow-md border border-neutral-100 max-h-160 divide-y divide-neutral-100 overflow-hidden"
          data-testid="lists-autocomplete-dropdown"
        >
          {/* Team Lists Button */}
          {(isMedScoutUser || isManagerUser) && (
            <button
              onClick={handleTeamListsSelect}
              className={`w-full px-6 py-3.5 text-sm font-medium text-left transition-all duration-150 flex items-center justify-between group ${
                isTeamListsSelected
                  ? 'bg-teal-100 text-teal-800 font-semibold'
                  : 'bg-gradient-to-r from-neutral-50 to-neutral-100/80 hover:from-neutral-100 hover:to-neutral-200/80 text-neutral-700'
              }`}
              role="option"
              aria-selected={isTeamListsSelected}
              data-testid="lists-autocomplete-team-lists"
            >
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 flex items-center justify-center rounded-full p-2 bg-teal-500/10 text-teal-600">
                  <Icon
                    icon={faUserGroup}
                    className="text-teal-600 text-xs"
                    aria-hidden="true"
                  />
                </div>
                <span>Team Lists</span>
              </div>
              <div className="flex items-center">
                {isTeamListsSelected && (
                  <Icon
                    icon={faCheck}
                    className="text-teal-600 flex-shrink-0 mr-2"
                    size="lg"
                    aria-hidden="true"
                  />
                )}
                <Icon
                  icon={faChevronRight}
                  className="text-neutral-400 group-hover:text-neutral-600 transition-colors duration-150"
                  size="sm"
                  aria-hidden="true"
                />
              </div>
            </button>
          )}

          {/* My Lists Section */}
          <div
            className="flex flex-col max-h-[min(400px,45vh)]"
            role="group"
            aria-labelledby="my-lists-header"
            data-testid="lists-autocomplete-my-section"
          >
            <div
              id="my-lists-header"
              className="sticky top-0 px-4 py-3 text-sm font-medium text-neutral-600 bg-neutral-50/80 border-b border-neutral-100 z-[101] backdrop-blur-sm"
            >
              My Lists
            </div>
            <div className="overflow-y-auto">
              {groupedLists.myLists.length > 0 ? (
                groupedLists.myLists.map((list) => (
                  <ListItem
                    key={list.id}
                    list={list}
                    isSelected={selectedList?.id === list.id}
                    onClick={() => handleListSelect(list)}
                  />
                ))
              ) : (
                <div
                  className="px-4 py-3 text-sm text-neutral-400 italic"
                  data-testid="lists-autocomplete-my-empty"
                >
                  {searchTerm
                    ? 'No matching lists found'
                    : 'No lists created yet'}
                </div>
              )}
            </div>
          </div>

          {/* Shared Lists Section */}
          <div
            className="flex flex-col max-h-[min(400px,45vh)]"
            role="group"
            aria-labelledby="shared-lists-header"
            data-testid="lists-autocomplete-shared-section"
          >
            <div
              id="shared-lists-header"
              className="sticky top-0 px-4 py-3 text-sm font-medium text-neutral-600 bg-neutral-50/80 border-b border-neutral-100 z-[101] backdrop-blur-sm"
            >
              Shared With Me
            </div>
            <div className="overflow-y-auto">
              {groupedLists.sharedLists.length > 0 ? (
                groupedLists.sharedLists.map((list) => (
                  <ListItem
                    key={list.id}
                    list={list}
                    isSelected={selectedList?.id === list.id}
                    onClick={() => handleListSelect(list)}
                    isShared
                  />
                ))
              ) : (
                <div
                  className="px-4 py-3 text-sm text-neutral-400 italic"
                  data-testid="lists-autocomplete-shared-empty"
                >
                  {searchTerm ? 'No matching lists found' : 'No shared lists'}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ListsAutocomplete;
